import { memo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { contentCollectionFormSelector } from '@air/feature-content-collection/redux';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPageViewNotification } from '~/components/AirNotifications/hooks/usePublicPageViewNotification';
import { ContentCollectionDropzone } from '~/components/ContentCollection/ContentCollectionDropzone';
import { ContentCollectionHero } from '~/components/ContentCollection/ContentCollectionHero';
import { ContentCollectionViewManager } from '~/components/ContentCollection/ContentCollectionViewManager';
import { useAlertOnUnsubmittedCCForm } from '~/components/ContentCollection/hooks/useAlertOnUnsubmittedCCForm';
import { useAccountContext } from '~/providers/AccountProvider';
import { MouseSelectionProvider } from '~/providers/MouseSelectionProvider';
import { PublicUploadUrlsManagerProvider } from '~/providers/PublicUploadUrlsManagerProvider';

export type ContentCollectionWrapperProps = {
  requireAccount: boolean;
};

export const ContentCollectionWrapper = memo(({ requireAccount }: ContentCollectionWrapperProps) => {
  const { shortId } = useShortIdContext();
  const { data: account } = useAccountContext();
  const { sendPublicPageViewEvent } = usePublicPageViewNotification();
  const form = useSelector(contentCollectionFormSelector);
  const selectionAreaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!requireAccount || (requireAccount && !!account)) {
      sendPublicPageViewEvent(shortId);
    }
  }, [account, requireAccount, sendPublicPageViewEvent, shortId]);

  useAlertOnUnsubmittedCCForm();

  return (
    <PublicUploadUrlsManagerProvider>
      <ContentCollectionDropzone>
        <div className="flex h-full flex-col ads:bg-ads-gray-4 bg-gray-4">
          {form?.workspace && <ContentCollectionHero account={account} workspace={form.workspace} />}
          {/* This div is the selection area (`#main`) — a plain click anywhere inside it clears
              the current selection (see useSelectedItemsMouseSelection). */}
          <MouseSelectionProvider selectionArea={selectionAreaRef}>
            <div className="flex-1 overflow-hidden" ref={selectionAreaRef}>
              <ContentCollectionViewManager requireAccount={requireAccount} />
            </div>
          </MouseSelectionProvider>
        </div>
      </ContentCollectionDropzone>
    </PublicUploadUrlsManagerProvider>
  );
});

ContentCollectionWrapper.displayName = 'ContentCollectionWrapper';
